import { useMemo, useCallback } from 'react';
import type { EventHandlers } from '@atlassian/jira-native-issue-table/src/common/types.tsx';
import { useSelectedIssueActions } from '../../../../controllers/selected-issue/hooks.tsx';
import { useOnDeleteIssue } from '../../../../services/on-delete-issue/index.tsx';
import { useIssueByFieldsRefetch } from '../../../../services/refetch-issue/index.tsx';

export const useEventHandlers = (
	connectionId: string | undefined,
	fieldSetIds: string[],
): EventHandlers => {
	const { setSelectedIssueByKey, deselectIssue } = useSelectedIssueActions();
	const onDeleteIssue = useOnDeleteIssue(connectionId);
	const onIssueByFieldsRefetch = useIssueByFieldsRefetch(fieldSetIds);

	const onIssueRefetch = onIssueByFieldsRefetch;
	// While we don't have a mutation setup for inline editing, use issue refetch as a substitute
	const onUpdateCache = onIssueByFieldsRefetch;
	const onIssueDelete = useCallback(
		(deletedIssueKey: string) => {
			const issueKeyToSelect = onDeleteIssue(deletedIssueKey);
			if (!issueKeyToSelect.length) {
				deselectIssue({
					shouldDebounce: false,
					shouldNotifyOnChange: false,
					isSelectedByUserInteraction: false,
				});
				return;
			}
			setSelectedIssueByKey(issueKeyToSelect, {
				shouldDebounce: false,
				shouldNotifyOnChange: false,
				isSelectedByUserInteraction: false,
			});
		},
		[onDeleteIssue, setSelectedIssueByKey, deselectIssue],
	);

	const onIssueLinkSelected = useCallback(
		(issueKey: string, viewActivity?: boolean): boolean => {
			setSelectedIssueByKey(issueKey, { viewActivity });
			return true;
		},
		[setSelectedIssueByKey],
	);

	return useMemo(
		() => ({
			onIssueRefetch,
			onUpdateCache,
			onIssueDelete,
			onIssueLinkSelected,
		}),
		[onIssueRefetch, onUpdateCache, onIssueDelete, onIssueLinkSelected],
	);
};
